import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, FileText, Plus, TrendingUp, UserRound } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StoreAppNudge } from "@/components/StoreAppNudge";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/pages/Manifests";

const STAT_ICONS = { manifests: FileText, revenue: TrendingUp, vehicles: Car, chauffeurs: UserRound };

export default function Dashboard() {
  const { company, quota } = useAuth();
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = () => {
      api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));
      api.get("/reports/digest").then(({ data }) => setDigest(data)).catch(() => {});
    };
    load();
    // The assistant can move a trip from the dock — these numbers must not go stale behind it.
    window.addEventListener("fm:data-changed", load);
    return () => window.removeEventListener("fm:data-changed", load);
  }, []);

  const stats = data?.stats;
  const limit = quota?.limit || 0;
  const used = quota?.used || 0;
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;

  return (
    <div data-testid="operator-dashboard" className="space-y-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Dispatch overview</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
            {company?.name}
          </h1>
        </div>
        <Button asChild className="bg-gold text-ink-900 hover:bg-gold-light">
          <Link to="/app/manifests/new" data-testid="dashboard-new-manifest-btn">
            <Plus className="mr-2 h-4 w-4" /> New trip
          </Link>
        </Button>
      </div>

      {error && <p data-testid="dashboard-error" className="text-sm text-red-400">{error}</p>}

      <StoreAppNudge />

      {digest && (
        <div data-testid="today-card" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Today · {digest.date}</p>
              <p className="mt-2 text-base text-slate-200">
                <span className="font-display text-2xl text-slate-50">{digest.today.count}</span> trip(s) worth{" "}
                {money(digest.today.booked_value)}
                {digest.today.airport_manifests
                  ? ` · ${digest.today.airport_manifests} airport manifest(s)` : ""}
              </p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Yesterday</p>
              <p className="mt-1 text-sm text-slate-300">
                {digest.yesterday_totals.trips} completed · {money(digest.yesterday_totals.revenue)}
                {digest.yesterday_totals.tips ? ` · ${money(digest.yesterday_totals.tips)} tips` : ""}
              </p>
            </div>
          </div>

          {digest.needs_driver.length > 0 ? (
            <div data-testid="today-needs-driver" className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-amber-300">
                Needs a chauffeur · {digest.needs_driver.length}
              </p>
              <div className="mt-3 space-y-1.5">
                {digest.needs_driver.slice(0, 5).map((t) => (
                  <Link key={t.id} to={`/app/manifests/${t.id}`} data-testid={`today-gap-${t.number}`}
                    className="block text-sm text-slate-300 transition-colors hover:text-gold">
                    <span className="font-mono text-xs text-gold">{t.pickup_time || "--:--"}</span>{" "}
                    #{t.number} · {t.customer_name} · {t.chauffeur_name || "unassigned"}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <p data-testid="today-all-covered" className="mt-5 text-sm text-slate-500">
              Every trip today has a chauffeur assigned.
            </p>
          )}

          {digest.outstanding.count > 0 && (
            <p data-testid="today-outstanding" className="mt-4 text-xs text-slate-500">
              {digest.outstanding.count} unpaid invoice(s) · {money(digest.outstanding.amount)} outstanding
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[
          { key: "manifests", label: "Trips", value: stats?.manifests ?? "—" },
          { key: "revenue", label: "Booked revenue", value: stats ? money(stats.revenue) : "—" },
          { key: "vehicles", label: "Vehicles", value: stats?.vehicles ?? "—" },
          { key: "chauffeurs", label: "Chauffeurs", value: stats?.chauffeurs ?? "—" },
        ].map((s) => {
          const Icon = STAT_ICONS[s.key];
          return (
            <div key={s.key} data-testid={`stat-${s.key}`} className="card-hover rounded-xl border border-ink-600 bg-ink-800 p-6">
              <Icon className="h-4 w-4 text-gold" />
              <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{s.label}</p>
              <p className="mt-1.5 font-display text-2xl font-semibold text-slate-50">{s.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div data-testid="quota-card" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Trips this week</p>
          <p className="mt-4 font-display text-2xl font-semibold text-slate-50">
            {limit ? `${used} / ${limit}` : `${used} used`}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {company?.plan_name} plan{limit ? " · resets every Monday" : " · unlimited"}
          </p>
          {limit > 0 && (
            <>
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-ink-600">
                <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${pct}%` }} />
              </div>
              {used >= limit && (
                <Link to="/app/plans" data-testid="quota-upgrade-link" className="mt-4 inline-block font-mono text-xs text-gold">
                  This week is used up — go unlimited from $1.99/week →
                </Link>
              )}
            </>
          )}
        </div>

        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Recent trips</p>
            <Link to="/app/manifests" className="text-xs text-slate-400 hover:text-gold">View all</Link>
          </div>
          <div className="mt-5 divide-y divide-ink-600">
            {(data?.recent || []).length === 0 && (
              <p data-testid="dashboard-empty" className="py-6 text-sm text-slate-500">
                No trips yet. Create your first one — you have {limit || "unlimited"} available.
              </p>
            )}
            {(data?.recent || []).map((m) => (
              <Link
                key={m.id}
                to={`/app/manifests/${m.id}`}
                data-testid={`recent-manifest-${m.number}`}
                className="flex items-center justify-between gap-4 py-3.5 transition-colors hover:text-gold"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">#{m.number} · {m.customer_name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {m.trip_date} {m.pickup_time} · {m.pickup?.address || "—"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-mono text-xs text-slate-400">{money(m.total_charge)}</span>
                  <StatusPill status={m.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
